/**
 * Environment-aware logging utility
 * Logs only in development, suppresses in production
 */

import { environment } from '../../../environments/environment';

/**
 * Logger that respects environment settings
 * Prevents console pollution in production builds
 */
export class EnvironmentLogger {
  /**
   * Log informational message (development only)
   * @param message Primary message
   * @param args Additional arguments to log
   */
  static log(message: string, ...args: any[]): void {
    if (!environment.production) {
      console.info(message, ...args);
    }
  }

  /**
   * Log warning message (development only)
   * @param message Warning message
   * @param args Additional arguments to log
   */
  static warn(message: string, ...args: any[]): void {
    if (!environment.production) {
      console.warn(message, ...args);
    }
  }

  /**
   * Log error message (always logged, even in production)
   * Errors should always be captured for debugging
   * @param message Error message
   * @param args Additional arguments to log
   */
  static error(message: string, ...args: any[]): void {
    console.error(message, ...args);
  }

  /**
   * Log debug message with context (development only)
   * @param context Context identifier (e.g., 'AudioService', 'VAD')
   * @param message Debug message
   * @param args Additional arguments to log
   */
  static debug(context: string, message: string, ...args: any[]): void {
    if (!environment.production) {
      console.debug(`[${context}]`, message, ...args);
    }
  }

  /**
   * Log grouped messages (development only)
   * Useful for debugging complex operations
   * @param label Group label
   * @param callback Function containing grouped logs
   */
  static group(label: string, callback: () => void): void {
    if (!environment.production) {
      console.group(label);
      callback();
      console.groupEnd();
    }
  }
}
